<script setup lang="ts">
import {
  type ComputedRef,
  computed,
  nextTick,
  shallowRef,
  unref,
  useId,
} from 'vue'
import BModal from './BModal.vue'
import BButton from './bButton.vue'
import { EModalPosition } from '@/services/application/interfaces'
import { bgColors } from '@/services/application/constantes'

import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type ConfirmProps = {
  message?: string | ComputedRef<string> | null
  confirmCallback?: () => unknown | Promise<unknown>
  cancelCallback?: () => unknown | Promise<unknown>
  position?: keyof typeof EModalPosition
  confirmButtonColor?: keyof typeof bgColors
  confirmDisabled?: boolean
  dataCy?: string
}

const props = withDefaults(defineProps<ConfirmProps>(), {
  message: null,
  confirmCallback: () => null,
  cancelCallback: () => null,
  position: EModalPosition.bottom,
  confirmButtonColor: 'error',
  confirmDisabled: false,
  dataCy: 'confirm-button',
})

const isOpen = defineModel<boolean>('isOpen', { default: false })
const titleId = useId()
const messageId = useId()
const isConfirming = shallowRef(false)

const modalExitMs: Record<string, number> = {
  base: 180,
  bottom: 280,
  top: 280,
  topFull: 280,
  right: 280,
  left: 280,
}

const closeModal = () => {
  isOpen.value = false
  props.cancelCallback()
}

const confirmModal = async () => {
  if (props.confirmDisabled || isConfirming.value) return
  isConfirming.value = true
  // Close and let AnimatePresence finish exiting before the callback.
  // Otherwise a parent unmount (list splice / navigation) leaves the
  // Teleport overlay stuck on the page.
  isOpen.value = false
  try {
    await nextTick()
    await new Promise<void>(resolve => {
      window.setTimeout(resolve, modalExitMs[props.position] ?? 180)
    })
    await props.confirmCallback()
  }
  finally {
    isConfirming.value = false
  }
}

const onModalModelUpdate = (value: boolean) => {
  if (value) {
    isOpen.value = true
    return
  }
  // Backdrop / Escape — only run cancel when we were still open
  if (isOpen.value) {
    isOpen.value = false
    props.cancelCallback()
  }
}

const resolvedMessage = computed(() => {
  const message = unref(props.message)
  return message || t('components.confirmationModal.message')
})
</script>

<template>
  <BModal
    :model-value="isOpen"
    :position="props.position"
    :have-header="false"
    role="alertdialog"
    :aria-modal="true"
    :aria-labelledby="titleId"
    :aria-describedby="messageId"
    @update:model-value="onModalModelUpdate"
  >
    <template #default>
      <slot name="message">
        <h2 :id="titleId" class="sr-only">
          {{ t('components.confirmationModal.title') }}
        </h2>
        <p :id="messageId" class="flex justify-center text-xl whitespace-pre-line">
          {{ resolvedMessage }}
        </p>
      </slot>
      <slot name="afterMessage" />
    </template>
    <template #footer>
      <slot name="footer">
        <div class="flex justify-center">
          <BButton
            class="mx-1"
            size="dense"
            bg="base"
            @click="closeModal"
          >
            {{ $t('components.confirmationModal.cancel') }}
          </BButton>
          <BButton
            class="mx-1"
            size="dense"
            :bg="confirmButtonColor"
            :disabled="confirmDisabled"
            :data-cy="dataCy"
            @click="confirmModal"
          >
            {{ $t('components.confirmationModal.confirm') }}
          </BButton>
        </div>
      </slot>
    </template>
  </BModal>
</template>
